import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { UserPlus, Search, Filter, MoreVertical, Trash2, ArrowUpDown, UserCheck, X, ChevronDown, ChevronLeft, ChevronRight, Plus, Linkedin, Facebook, Instagram, Twitter, Globe, Link2, PlusCircle, MinusCircle } from 'lucide-react';
import { PhoneInputField } from '@/components/ui/phone-input';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { leadService } from '@/lib/leadService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { motion, AnimatePresence } from 'framer-motion';
import type { CrmLead } from '@/types/crm-lead';

interface LeadsProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const Leads: React.FC<LeadsProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const { currentWorkspace } = useWorkspace();
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);
  
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<CrmLead | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('');
  const [designation, setDesignation] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyCategory, setCompanyCategory] = useState('');
  const [emailType, setEmailType] = useState<'work' | 'personal' | 'other'>('work');
  const [phoneType, setPhoneType] = useState<'work' | 'personal' | 'other'>('work');
  const [website, setWebsite] = useState('');
  const [socialLinks, setSocialLinks] = useState<{platform: string, url: string}[]>([{platform: 'linkedin', url: ''}]);
  const [address, setAddress] = useState<{ country?: string; state?: string; city?: string; zip?: string; streetAddress?: string }>({});
  const [additionalEmails, setAdditionalEmails] = useState<{email: string, type: 'work' | 'personal' | 'other'}[]>([]);
  const [additionalPhones, setAdditionalPhones] = useState<{phone: string, type: 'work' | 'personal' | 'other'}[]>([]);
  const [submitting, setSubmitting] = useState(false);
  
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState<string | null>(null);

  const handleMobileMenuToggle = () => {
    const crmMenuItem = navigationItems.find(item => item.id === 'crm');
    if (crmMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmMenuItem.subItems, crmMenuItem.label);
    }
  };

  const loadLeads = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      setLoading(true);
      const data = await leadService.getLeadsByWorkspace(currentWorkspace.id);
      setLeads(data);
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    const leadId = searchParams.get('leadId');
    if (leadId && leads.length > 0) {
      const targetLead = leads.find(l => l.id === leadId);
      if (targetLead) {
        openEditModal(targetLead);
      }
    }
  }, [searchParams, leads]);

  const handleCloseDialog = () => {
    setAddLeadOpen(false);
    setTimeout(() => {
      setEditingLead(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setSource('');
      setDesignation('');
      setCompanyName('');
      setCompanyCategory('');
      setEmailType('work');
      setPhoneType('work');
      setWebsite('');
      setSocialLinks([{platform: 'linkedin', url: ''}]);
      setAddress({});
      setAdditionalEmails([]);
      setAdditionalPhones([]);
    }, 200);
  };

  const openEditModal = (lead: CrmLead) => {
    setEditingLead(lead);
    setFirstName(lead.first_name || '');
    setLastName(lead.last_name || '');
    setEmail(lead.email || '');
    setPhone(lead.phone || '');
    setSource(lead.source || '');
    setDesignation(lead.designation || '');
    setCompanyName(lead.company_name || '');
    setCompanyCategory(lead.company_category || '');
    setEmailType((lead.email_type as any) || 'work');
    setPhoneType((lead.phone_type as any) || 'work');
    setWebsite(lead.website || '');
    
    const links: {platform: string, url: string}[] = [];
    if (lead.social_media) {
      Object.entries(lead.social_media).forEach(([platform, url]) => {
        if (url) links.push({ platform: platform as string, url: url as string });
      });
    }
    setSocialLinks(links.length > 0 ? links : [{platform: 'linkedin', url: ''}]);
    
    setAddress(lead.address || {});
    setAdditionalEmails(lead.additional_emails || []);
    setAdditionalPhones(lead.additional_phones || []);
    setAddLeadOpen(true);
  };

  const handleSaveLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWorkspace || !firstName.trim() || !email.trim()) return;

    try {
      setSubmitting(true);
      const { data: { user } } = await import('@/lib/supabase').then(m => m.supabase.auth.getUser());
      if (!user) throw new Error('Not authenticated');

      const socialMediaObj: Record<string, string> = {};
      socialLinks.forEach(link => {
        if (link.url.trim() && link.platform) {
          socialMediaObj[link.platform] = link.url.trim();
        }
      });

      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        email_type: emailType,
        phone: phone.trim(),
        phone_type: phoneType,
        website: website.trim() || undefined,
        source: source.trim() || undefined,
        designation: designation.trim() || undefined,
        company_name: companyName.trim() || undefined,
        company_category: companyCategory.trim() || undefined,
        address: Object.keys(address).length > 0 ? address : undefined,
        social_media: Object.keys(socialMediaObj).length > 0 ? socialMediaObj : undefined,
        additional_emails: additionalEmails.length > 0 ? additionalEmails : undefined,
        additional_phones: additionalPhones.length > 0 ? additionalPhones : undefined,
      };

      if (editingLead) {
        await leadService.updateLead(editingLead.id, payload);
        toast.success('Lead updated successfully');
      } else {
        await leadService.createLead(currentWorkspace.id, user.id, {
          ...payload,
          status: 'new'
        });
        toast.success('Lead created successfully');
      }

      handleCloseDialog();
      await loadLeads();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${editingLead ? 'update' : 'create'} lead`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await leadService.deleteLead(deleteId);
      toast.success('Lead deleted');
      setDeleteId(null);
      await loadLeads();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const handleConvertToClient = async (leadId: string) => {
    if (!currentWorkspace) return;
    try {
      setConverting(leadId);
      await leadService.convertLeadToClient(leadId, currentWorkspace.id);
      toast.success('Lead converted to client successfully');
      await loadLeads();
    } catch (error: any) {
      toast.error(error.message || 'Failed to convert lead');
    } finally {
      setConverting(null);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const term = (isMobile ? mobileSearchQuery : searchQuery).toLowerCase();
    const matchesSearch = 
      (lead.first_name || '').toLowerCase().includes(term) ||
      (lead.last_name || '').toLowerCase().includes(term) ||
      (lead.email || '').toLowerCase().includes(term);
    
    if (statusFilter === 'all') return matchesSearch;
    return matchesSearch && lead.status === statusFilter;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const paginatedLeads = filteredLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Leads | CRM | Brandify</title>
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={() => setAddLeadOpen(true)}
          searchValue={mobileSearchQuery}
          onSearchChange={setMobileSearchQuery}
          searchPlaceholder="Search leads..."
          showSearch={true}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        {/* Unified Header */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Leads</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your leads and convert them to clients
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search — collapsible */}
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="search-open"
                    className="relative flex items-center h-10"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search leads..."
                      onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border focus:ring-1 focus:ring-workspace-accent/30"
                    />
                    <button
                      onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-icon"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border hover:bg-muted transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Filter Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 h-10 rounded-lg border transition-colors whitespace-nowrap ${statusFilter !== 'all'
                    ? 'bg-workspace-accent text-white border-transparent'
                    : 'bg-card border-border hover:bg-muted'
                  }`}>
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">{statusFilter === 'all' ? 'Filter' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover border-border">
                <DropdownMenuItem onClick={() => setStatusFilter('all')} className="flex items-center justify-between">
                  All Status {statusFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('new')} className="flex items-center justify-between">
                  New {statusFilter === 'new' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('contacted')} className="flex items-center justify-between">
                  Contacted {statusFilter === 'contacted' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('qualified')} className="flex items-center justify-between">
                  Qualified {statusFilter === 'qualified' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('unqualified')} className="flex items-center justify-between">
                  Unqualified {statusFilter === 'unqualified' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('converted')} className="flex items-center justify-between">
                  Converted {statusFilter === 'converted' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Add Button */}
            <button
              onClick={() => setAddLeadOpen(true)}
              className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span className="font-medium">Add Lead</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-white dark:bg-card overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-6 py-3.5 text-xs font-semibold text-muted-foreground w-12 text-center">
                  <Checkbox 
                    className="rounded flex mx-auto border-muted-foreground/30 data-[state=checked]:bg-workspace-accent data-[state=checked]:border-workspace-accent"
                    checked={filteredLeads.length > 0 && selectedLeads.length === filteredLeads.length}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedLeads(filteredLeads.map(l => l.id));
                      } else {
                        setSelectedLeads([]);
                      }
                    }}
                  />
                </th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Name
                </th>
                <th className="text-center px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-24">Social</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Phone</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Company</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Location</th>
                <th className="text-right px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-l border-border w-12">
                  <ArrowUpDown className="w-4 h-4 text-muted-foreground mx-auto cursor-pointer" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="w-4 h-4 rounded bg-muted/40 mx-auto" /></td>
                    <td className="px-6 py-4"><div className="w-32 h-10 rounded bg-muted/40" /></td>
                    <td className="px-6 py-4"><div className="w-6 h-6 rounded bg-muted/40 mx-auto" /></td>
                    <td className="px-6 py-4"><div className="w-40 h-10 rounded bg-muted/40" /></td>
                    <td className="px-6 py-4"><div className="w-32 h-10 rounded bg-muted/40" /></td>
                    <td className="px-6 py-4"><div className="w-32 h-10 rounded bg-muted/40" /></td>
                    <td className="px-6 py-4"><div className="w-24 h-10 rounded bg-muted/40" /></td>
                    <td className="px-6 py-4"><div className="w-8 h-8 rounded bg-muted/40 ml-auto" /></td>
                  </tr>
                ))
              ) : filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-muted/20 flex items-center justify-center">
                        <UserPlus className="w-6 h-6 text-muted-foreground/50" />
                      </div>
                      <p>No leads found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedLeads.map((lead) => (
                  <tr 
                    key={lead.id} 
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('.no-row-click')) return;
                      openEditModal(lead);
                    }}
                  >
                    <td className="px-6 py-4 align-top pt-5 no-row-click">
                      <Checkbox 
                        className="rounded mx-auto block border-muted-foreground/30 data-[state=checked]:bg-workspace-accent data-[state=checked]:border-workspace-accent"
                        checked={selectedLeads.includes(lead.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedLeads([...selectedLeads, lead.id]);
                          } else {
                            setSelectedLeads(selectedLeads.filter(id => id !== lead.id));
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="font-medium text-foreground">{lead.first_name} {lead.last_name}</div>
                        {lead.designation && <div className="text-sm text-muted-foreground font-light">{lead.designation}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top text-center pt-5">
                      <div className="flex justify-center gap-2 flex-wrap max-w-[80px] mx-auto">
                        {lead.social_media && Object.entries(lead.social_media).map(([platform, url]) => {
                          if (!url) return null;
                          let Icon = Link2;
                          let color = "text-muted-foreground";
                          if (platform === 'linkedin') { Icon = Linkedin; color = "text-[#0077b5]"; }
                          else if (platform === 'twitter' || platform === 'x') { Icon = Twitter; color = "text-foreground"; }
                          else if (platform === 'facebook') { Icon = Facebook; color = "text-[#1877F2]"; }
                          else if (platform === 'instagram') { Icon = Instagram; color = "text-[#E4405F]"; }
                          
                          return (
                            <a key={platform} href={url} target="_blank" rel="noreferrer" className={`inline-block hover:opacity-80 transition-opacity ${color}`}>
                              <Icon className="w-4 h-4" />
                            </a>
                          );
                        })}
                        {(!lead.social_media || Object.keys(lead.social_media).length === 0) && <span className="text-muted-foreground">-</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col gap-2">
                        {lead.email && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-muted text-muted-foreground rounded tracking-wide capitalize">{lead.email_type || 'work'}</span>
                            <span className="text-sm text-foreground truncate max-w-[200px] xl:max-w-[250px]">{lead.email}</span>
                          </div>
                        )}
                        {lead.additional_emails?.map((ae, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-muted text-muted-foreground rounded tracking-wide capitalize">{ae.type}</span>
                            <span className="text-sm text-foreground truncate max-w-[200px] xl:max-w-[250px]">{ae.email}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col gap-2">
                        {lead.phone && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-muted text-muted-foreground rounded tracking-wide capitalize">{lead.phone_type || 'work'}</span>
                            <span className="text-sm text-foreground truncate max-w-[200px] xl:max-w-[250px]">{lead.phone}</span>
                          </div>
                        )}
                        {lead.additional_phones?.map((ap, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-muted text-muted-foreground rounded tracking-wide capitalize">{ap.type}</span>
                            <span className="text-sm text-foreground truncate max-w-[200px] xl:max-w-[250px]">{ap.phone}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="font-medium text-foreground text-sm">{lead.company_name || 'No Company'}</div>
                        {lead.company_category && <div className="text-xs text-muted-foreground">{lead.company_category}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col gap-1">
                        {lead.address?.city || lead.address?.country ? (
                          <>
                            {lead.address?.city && <div className="font-medium text-foreground text-sm">{lead.address.city}</div>}
                            {lead.address?.country && <div className="text-xs text-muted-foreground">{lead.address.country}</div>}
                          </>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right border-l border-border align-top pt-5 no-row-click">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                            <MoreVertical className="w-4 h-4 mx-auto" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {lead.status !== 'converted' && (
                            <DropdownMenuItem 
                              onClick={() => handleConvertToClient(lead.id)}
                              disabled={converting === lead.id}
                            >
                              <UserCheck className="w-4 h-4 text-workspace-accent" />
                              {converting === lead.id ? 'Converting...' : 'Convert to Client'}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); openEditModal(lead); }}
                          >
                            Edit details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            destructive
                            onClick={() => setDeleteId(lead.id)}
                          >
                            <Trash2 className="w-4 h-4" /> Delete lead
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination Controls */}
          {filteredLeads.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-transparent">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span>Show</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="200">200</SelectItem>
                      <SelectItem value="500">500</SelectItem>
                    </SelectContent>
                  </Select>
                  <span>per page</span>
                </div>
                <span>Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredLeads.length)} of {filteredLeads.length} leads</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="h-8 w-8 p-0">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-sm font-medium mx-2">Page {currentPage} of {totalPages || 1}</div>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages || totalPages === 0} className="h-8 w-8 p-0">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Lead Dialog */}
      <Dialog open={addLeadOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="sm:max-w-[800px] bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveLead} className="max-h-[80vh] overflow-y-auto px-1 flex flex-col">
            <div className="grid grid-cols-2 gap-8 pt-4">
              {/* Left Column: Lead Details */}
              <div className="space-y-0">
                <Accordion type="multiple" defaultValue={['general']} className="w-full">
                  <AccordionItem value="general" className="border-border">
                    <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                      General
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>First Name <span className="text-red-500">*</span></Label>
                          <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" required />
                        </div>
                        <div className="space-y-2">
                          <Label>Last Name</Label>
                          <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Doe" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Lead Source</Label>
                        <Input value={source} onChange={e => setSource(e.target.value)} placeholder="e.g. Website, Referral" />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="contact-info" className="border-border">
                    <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                      Contact Information
                    </AccordionTrigger>
                    <AccordionContent className="space-y-6 pb-4">
                      <div className="space-y-4">
                        <div className="flex gap-2 items-center">
                          <Input className="flex-1 h-9" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Default Email *" required />
                          <div className="w-[100px]">
                            <Select value={emailType} onValueChange={(val: any) => setEmailType(val)}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="work">Work</SelectItem>
                                <SelectItem value="personal">Personal</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {additionalEmails.map((ae, idx) => (
                          <div key={`email-${idx}`} className="flex gap-2 items-center">
                            <Input className="flex-1 h-9" type="email" value={ae.email} onChange={e => {
                              const newEmails = [...additionalEmails];
                              newEmails[idx].email = e.target.value;
                              setAdditionalEmails(newEmails);
                            }} placeholder="Additional Email" />
                            <div className="w-[100px]">
                              <Select value={ae.type} onValueChange={(val: any) => {
                                const newEmails = [...additionalEmails];
                                newEmails[idx].type = val;
                                setAdditionalEmails(newEmails);
                              }}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="work">Work</SelectItem>
                                  <SelectItem value="personal">Personal</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <button type="button" onClick={() => setAdditionalEmails(additionalEmails.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                              <MinusCircle className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        
                        <button type="button" onClick={() => setAdditionalEmails([...additionalEmails, {email: '', type: 'personal'}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                          <PlusCircle className="w-3 h-3" /> Add Email
                        </button>
                      </div>
                      
                      <div className="space-y-4 pt-2">
                        <div className="flex gap-2 items-center">
                          <PhoneInputField className="flex-1 h-9" value={phone} onChange={setPhone} placeholder="Default Phone" />
                          <div className="w-[100px]">
                            <Select value={phoneType} onValueChange={(val: any) => setPhoneType(val)}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="work">Work</SelectItem>
                                <SelectItem value="personal">Personal</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {additionalPhones.map((ap, idx) => (
                          <div key={`phone-${idx}`} className="flex gap-2 items-center">
                            <PhoneInputField className="flex-1 h-9" value={ap.phone} onChange={e => {
                              const newPhones = [...additionalPhones];
                              newPhones[idx].phone = e;
                              setAdditionalPhones(newPhones);
                            }} placeholder="Additional Phone" />
                            <div className="w-[100px]">
                              <Select value={ap.type} onValueChange={(val: any) => {
                                const newPhones = [...additionalPhones];
                                newPhones[idx].type = val;
                                setAdditionalPhones(newPhones);
                              }}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="work">Work</SelectItem>
                                  <SelectItem value="personal">Personal</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <button type="button" onClick={() => setAdditionalPhones(additionalPhones.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                              <MinusCircle className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        
                        <button type="button" onClick={() => setAdditionalPhones([...additionalPhones, {phone: '', type: 'personal'}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                          <PlusCircle className="w-3 h-3" /> Add Phone
                        </button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="social-profiles" className="border-border border-b-0">
                    <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                      Social Profiles
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pb-4">
                      {socialLinks.map((sl, idx) => (
                        <div key={`social-${idx}`} className="flex gap-2 items-center">
                          <div className="w-[120px]">
                            <Select value={sl.platform} onValueChange={(val: any) => {
                              const newLinks = [...socialLinks];
                              newLinks[idx].platform = val;
                              setSocialLinks(newLinks);
                            }}>
                              <SelectTrigger className="h-9"><SelectValue placeholder="Platform" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="linkedin">LinkedIn</SelectItem>
                                <SelectItem value="twitter">X (Twitter)</SelectItem>
                                <SelectItem value="facebook">Facebook</SelectItem>
                                <SelectItem value="instagram">Instagram</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="relative flex-1">
                            <Link2 className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                            <Input className="pl-9 h-9" value={sl.url} onChange={e => {
                              const newLinks = [...socialLinks];
                              newLinks[idx].url = e.target.value;
                              setSocialLinks(newLinks);
                            }} placeholder="Profile URL" />
                          </div>
                          <button type="button" onClick={() => setSocialLinks(socialLinks.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                            <MinusCircle className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                      
                      <button type="button" onClick={() => setSocialLinks([...socialLinks, {platform: 'linkedin', url: ''}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                        <PlusCircle className="w-3 h-3" /> Add Social Link
                      </button>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Right Column: Company Details */}
              <div className="space-y-4 border-l border-border pl-8 -ml-4">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">Company Details</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="e.g. Acme Corp" />
                  </div>
                  <div className="space-y-2">
                    <Label>Category / Industry</Label>
                    <Input value={companyCategory} onChange={e => setCompanyCategory(e.target.value)} placeholder="e.g. Oil & Energy" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Website</Label>
                  <div className="relative">
                    <Globe className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input className="pl-9" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://example.com" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={designation} onChange={e => setDesignation(e.target.value)} placeholder="e.g. Certified Life Coach" />
                </div>
                
                <Accordion type="multiple" className="w-full pt-4 border-t border-border mt-6">
                  <AccordionItem value="address" className="border-none">
                    <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-0 pb-4">
                      Address
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pb-0">
                      <div className="space-y-2">
                        <Label>Street Address</Label>
                        <Input value={address.streetAddress || ''} onChange={e => setAddress({...address, streetAddress: e.target.value})} placeholder="123 Main St" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>City</Label>
                          <Input value={address.city || ''} onChange={e => setAddress({...address, city: e.target.value})} placeholder="New York" />
                        </div>
                        <div className="space-y-2">
                          <Label>State / Province</Label>
                          <Input value={address.state || ''} onChange={e => setAddress({...address, state: e.target.value})} placeholder="NY" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>ZIP / Postal Code</Label>
                          <Input value={address.zip || ''} onChange={e => setAddress({...address, zip: e.target.value})} placeholder="10001" />
                        </div>
                        <div className="space-y-2">
                          <Label>Country</Label>
                          <Input value={address.country || ''} onChange={e => setAddress({...address, country: e.target.value})} placeholder="United States" />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>

            <DialogFooter className="pt-6 border-t border-border mt-6">
              <Button type="button" variant="ghost" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" className="bg-workspace-accent hover:bg-workspace-accent-hover text-white" disabled={submitting || !firstName.trim() || !email.trim()}>
                {submitting ? (editingLead ? 'Saving...' : 'Adding...') : (editingLead ? 'Save Changes' : 'Add Lead')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Lead Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent className="sm:max-w-[425px] bg-card border-border">
          <DialogHeader>
            <DialogTitle>Delete Lead</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">Are you sure you want to delete this lead? This action cannot be undone.</p>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={handleDeleteLead} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
